// features/projects/templates/professional/widgets/where-things-break.js
// ═══════════════════════════════════════════════════════════
// WHERE THINGS BREAK — Tier-A widget
//
// Q: "Which findings break the parallel claim, and what does
//     each break tell us?"
// Surfaces DIVERGENT + KILLED + RESPECIFIED counts with
// per-break interpretation rows. Breaks are information, not
// failures.
// ═══════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { L, FONT } from '../tokens';
import { useWidgetData } from './data-provider';
import { DYNAMICS } from '../../../../../lib/continuum/simulation/methodology';
import { WidgetShell, LayerSection, LayerAction, CaveatStrip } from './findings-status';

const DYN_BY_KEY = Object.fromEntries(DYNAMICS.map(d => [d.key, d]));

export default function WhereThingsBreakWidget() {
  const { loading, comparisons } = useWidgetData();

  const { divergent, killed, respecified } = useMemo(() => ({
    divergent: comparisons.filter(c => (c.verdict || '').toUpperCase() === 'DIVERGENT'),
    killed: comparisons.filter(c => c.phase_a_status === 'killed'),
    respecified: comparisons.filter(c => c.phase_a_status === 'survived-respecified'),
  }), [comparisons]);

  if (loading) return <WidgetShell label="Where Things Break"><Loading /></WidgetShell>;

  if (divergent.length === 0 && killed.length === 0 && respecified.length === 0) {
    return (
      <WidgetShell label="Where Things Break">
        <div style={{ fontSize: 12, color: L.mid, lineHeight: 1.55, fontFamily: FONT.sans }}>
          No breaks recorded yet. Either everything tested has held, or the
          test set is too small to surface domain-specificity. Both are
          interpretive risks worth tracking.
        </div>
      </WidgetShell>
    );
  }

  const story = composeStory(divergent, killed, respecified);
  const action = composeAction(divergent);
  const caveat = composeCaveat(divergent);

  return (
    <WidgetShell label={`Where Things Break (${divergent.length + killed.length})`}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap',
        marginBottom: 4,
      }}>
        {divergent.length > 0 && <Stat color={L.terra} count={divergent.length} label="divergent" />}
        {killed.length > 0 && <Stat color={L.terra} count={killed.length} label="killed by Phase A" />}
        {respecified.length > 0 && <Stat color={L.gold} count={respecified.length} label="respecified" />}
      </div>

      <LayerSection label="Story">{story}</LayerSection>
      <LayerSection label="Takeaway" accent={L.gold}>
        Breaks are information, not failure. They constrain the universality claim and surface where the parallel needs domain-specific framing. Each unreviewed break is a candidate for either substantive divergence or operationalization mismatch — Phase A distinguishes them.
      </LayerSection>

      {divergent.length > 0 && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px dashed ${L.b1}` }}>
          <div style={{
            fontFamily: FONT.mono, fontSize: 8, letterSpacing: '0.14em',
            color: L.dim, textTransform: 'uppercase', marginBottom: 8,
          }}>
            Per-break detail
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {divergent.slice().sort((a, b) =>
              Math.abs(Number(b.effect_size) || 0) - Math.abs(Number(a.effect_size) || 0)
            ).map(c => <BreakRow key={c.id} comp={c} />)}
          </div>
        </div>
      )}

      {action && <LayerAction accent={L.sage} label={action.label} why={action.why} />}
      {caveat && <CaveatStrip text={caveat} />}
    </WidgetShell>
  );
}

function Stat({ color, count, label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
      <span style={{
        fontFamily: FONT.mono, fontSize: 22, color, fontWeight: 300,
        letterSpacing: '-0.01em',
      }}>{count}</span>
      <span style={{ fontSize: 11, color: L.mid, fontFamily: FONT.sans }}>{label}</span>
    </span>
  );
}

function BreakRow({ comp }) {
  const dyn = DYN_BY_KEY[comp.dynamic_key];
  const dynLabel = dyn?.label || comp.dynamic_key;
  const d = Number(comp.effect_size) || 0;
  const direction = d > 0 ? 'eco-side higher' : 'human-side higher';
  const interp = d > 0
    ? 'ecological systems show the larger ratio — likely capacity differences (biology, longer time series, less anticipation)'
    : 'human systems show the larger ratio — possible substrate-specific amplification or anticipatory dynamics';
  return (
    <div style={{
      padding: '8px 10px',
      background: 'rgba(196,80,80,.04)',
      border: `1px solid ${L.terra}30`,
      borderRadius: 4,
    }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 4,
      }}>
        <span style={{ color: L.text, fontFamily: FONT.sans, fontSize: 12, fontWeight: 500 }}>
          {dynLabel}
        </span>
        <span style={{ fontFamily: FONT.mono, fontSize: 10, color: L.terra }}>
          d={d.toFixed(2)} · {direction}
        </span>
        {!comp.phase_a_status && (
          <span style={{
            fontFamily: FONT.mono, fontSize: 8, color: L.gold, letterSpacing: '0.06em',
            border: `1px solid ${L.gold}50`, padding: '1px 6px', borderRadius: 2,
            textTransform: 'uppercase',
          }}>
            Phase A not run
          </span>
        )}
      </div>
      <div style={{ fontFamily: FONT.sans, fontSize: 11, color: L.mid, lineHeight: 1.5 }}>
        {interp}.
      </div>
    </div>
  );
}

function composeStory(divergent, killed, respecified) {
  const parts = [];
  if (divergent.length > 0) {
    const top = divergent.slice().sort((a, b) =>
      Math.abs(Number(b.effect_size) || 0) - Math.abs(Number(a.effect_size) || 0))[0];
    const dyn = DYN_BY_KEY[top.dynamic_key];
    parts.push(`The largest divergence is ${dyn?.label || top.dynamic_key} (d=${(Number(top.effect_size) || 0).toFixed(2)}).`);
  }
  if (killed.length > 0) parts.push(`Phase A killed ${killed.length} claim${killed.length === 1 ? '' : 's'} — those are evidence against, not null results.`);
  if (respecified.length > 0) parts.push(`${respecified.length} finding${respecified.length === 1 ? '' : 's'} survived Phase A only after the claim was narrowed.`);
  return parts.join(' ') || 'No active breaks.';
}
function composeAction(divergent) {
  const unreviewed = divergent.filter(c => !c.phase_a_status);
  if (unreviewed.length === 0) return null;
  const top = unreviewed.slice().sort((a, b) =>
    Math.abs(Number(b.effect_size) || 0) - Math.abs(Number(a.effect_size) || 0))[0];
  const dyn = DYN_BY_KEY[top.dynamic_key];
  return {
    label: `Run Phase A on ${dyn?.label || top.dynamic_key}`,
    why: `Largest unreviewed divergence (d=${(Number(top.effect_size) || 0).toFixed(2)}). Phase A will distinguish real mechanism difference from operationalization artifact.`,
  };
}
function composeCaveat(divergent) {
  const noPhaseA = divergent.filter(c => !c.phase_a_status).length;
  if (noPhaseA === 0) return 'Every divergent finding has been Phase A red-teamed — these breaks are defensible.';
  return `${noPhaseA} divergent finding${noPhaseA === 1 ? ' has' : 's have'} not been Phase A red-teamed. Treat as suggestive of domain-specificity, not as confirmed evidence.`;
}
function Loading() {
  return <div style={{ fontSize: 12, color: L.dim, fontStyle: 'italic', fontFamily: FONT.sans }}>Loading…</div>;
}
